import React from "react";
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { LegacyTokens } from "../../../theme/b13Tokens";

// Batch 2 §2.6 — revealed beneath the Program Status row when status ===
// "maintenance". Persists to programs.maintenance_frequency (existing
// string column — no migration). Encodes as "N unit" strings; legacy
// enum values (weekly/biweekly/monthly/quarterly) are round-tripped on
// load. Value survives toggling status away and back within one edit
// session because parent formik state stays mounted.

export type Unit = "day" | "week" | "month";

export const UNIT_LABELS: Record<Unit, string> = {
  day: "day",
  week: "week",
  month: "month",
};

/**
 * Parse whatever's currently in maintenance_frequency into {n, unit}.
 * Handles the new "N unit" format AND the legacy enum strings that
 * existed before this control landed.
 */
export function parseMaintenance(raw: string | null | undefined): { n: number; unit: Unit } {
  const v = String(raw ?? "").trim().toLowerCase();
  if (!v) return { n: 1, unit: "week" };

  // Legacy enum values.
  if (v === "weekly")    return { n: 1, unit: "week" };
  if (v === "biweekly")  return { n: 2, unit: "week" };
  if (v === "monthly")   return { n: 1, unit: "month" };
  if (v === "quarterly") return { n: 3, unit: "month" };

  // "N unit" or "N units" format.
  const m = v.match(/^(\d+)\s*(day|days|week|weeks|month|months)/);
  if (m) {
    const n = Math.max(1, parseInt(m[1], 10));
    const u = m[2].replace(/s$/, "") as Unit;
    return { n, unit: u };
  }

  // Unknown legacy string — default; the raw value is preserved on the
  // parent form field until the user makes a choice.
  return { n: 1, unit: "week" };
}

export function stringifyMaintenance(n: number, unit: Unit): string {
  const N = Math.max(1, Math.floor(n));
  return `${N} ${unit}${N === 1 ? "" : "s"}`;
}

export type MaintenanceIntervalSubControlProps = {
  value: string | null | undefined;
  onChange: (next: string) => void;
  disabled?: boolean;
};

export default function MaintenanceIntervalSubControl({
  value, onChange, disabled = false,
}: MaintenanceIntervalSubControlProps) {
  const { n: initialN, unit: initialUnit } = parseMaintenance(value);
  const [n, setN]       = React.useState<number>(initialN);
  const [unit, setUnit] = React.useState<Unit>(initialUnit);

  // Reconcile if the parent value changes out from under us (e.g. edit
  // load of an existing program).
  React.useEffect(() => {
    const parsed = parseMaintenance(value);
    setN(parsed.n);
    setUnit(parsed.unit);
    // Intentionally only sync when the incoming string identity changes,
    // not on every render — otherwise typing 12 would repeatedly reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const push = (nn: number, uu: Unit) => onChange(stringifyMaintenance(nn, uu));

  return (
    <Stack
      direction="row" spacing={1.5} alignItems="center"
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${LegacyTokens.line}`,
        backgroundColor: LegacyTokens.canvas2,
      }}
      data-testid="maintenance-interval-subcontrol"
    >
      <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2 }}>Probe every</Typography>
      <TextField
        size="small" type="number"
        value={n}
        disabled={disabled}
        inputProps={{ min: 1, style: { width: 56, textAlign: "right" } }}
        onChange={(e) => {
          const next = Math.max(1, Number(e.target.value) || 1);
          setN(next);
          push(next, unit);
        }}
      />
      <FormControl size="small" sx={{ minWidth: 110 }}>
        <InputLabel>Unit</InputLabel>
        <Select
          label="Unit"
          value={unit}
          disabled={disabled}
          onChange={(e) => {
            const u = e.target.value as Unit;
            setUnit(u);
            push(n, u);
          }}
        >
          <MenuItem value="day">day{n === 1 ? "" : "s"}</MenuItem>
          <MenuItem value="week">week{n === 1 ? "" : "s"}</MenuItem>
          <MenuItem value="month">month{n === 1 ? "" : "s"}</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );
}
